import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Phone } from "lucide-react";
import { AuthContext } from "../../App";
import {
  ensureVoiceReady,
  fetchDefaultFromNumber,
  placeOutboundCall,
  readCallOutcome,
} from "../../services/clickToCall";
import { primeVoiceAudio } from "../../services/voiceDevice";
import "./ClickToCallButton.css";

// UI phases for a single click-to-call interaction.
const PHASE = {
  IDLE: "idle",
  CONNECTING: "connecting",
  RINGING: "ringing",
  CONNECTED: "connected",
  ENDED: "ended",
  FAILED: "failed",
  FALLBACK: "fallback",
};

const OUTCOME_LABELS = {
  answered: "Connected",
  completed: "Connected",
  connected: "Connected",
  no_answer: "No answer",
  "no-answer": "No answer",
  busy: "Busy",
  failed: "Failed",
  voicemail: "Voicemail",
};

const OUTCOME_TONE = {
  answered: "ok",
  completed: "ok",
  connected: "ok",
  no_answer: "warn",
  "no-answer": "warn",
  busy: "warn",
  voicemail: "warn",
  failed: "bad",
};

function formatDuration(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

function getCallSid(connection) {
  if (!connection) return "";
  const params = connection.parameters || connection.customParameters || {};
  return params.CallSid || params.callSid || params.call_sid || "";
}

/**
 * In-app click-to-call button. Replaces a bare tel: link on lead / customer cards.
 * Brings the browser Voice device online on demand, dials the party through the
 * voice service, shows live call state (ringing / connected / duration / hang-up),
 * and reflects the server-recorded outcome on end. Falls back to a tel: link when
 * in-app calling is unavailable (no outbound number, mic denied, voice disabled).
 */
export default function ClickToCallButton({
  phone,
  className = "",
  label = "Call",
  calleeName = "",
  fromNumber: fromNumberProp,
  onOutcome,
}) {
  const auth = useContext(AuthContext) || {};
  const tenantId = auth.tenant?.id;
  const userId = auth.user?.id;

  const [phase, setPhase] = useState(PHASE.IDLE);
  const [seconds, setSeconds] = useState(0);
  const [outcome, setOutcome] = useState("");
  const [hint, setHint] = useState("");

  const connectionRef = useRef(null);
  const timerRef = useRef(null);
  const acceptedRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearInterval(timerRef.current);
      const conn = connectionRef.current;
      if (conn && typeof conn.disconnect === "function") {
        try {
          conn.disconnect();
        } catch {
          // best-effort teardown
        }
      }
    };
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const settleOutcome = useCallback(
    (clientOutcome, connection) => {
      stopTimer();
      const sid = getCallSid(connection);
      let resolved = clientOutcome;
      if (mountedRef.current) {
        setOutcome(resolved);
        setPhase(PHASE.ENDED);
      }
      // Read back the authoritative, server-derived outcome (best-effort).
      if (sid) {
        setTimeout(async () => {
          const serverOutcome = await readCallOutcome(sid);
          if (serverOutcome && mountedRef.current) {
            resolved = serverOutcome;
            setOutcome(serverOutcome);
          }
          if (onOutcome) onOutcome({ callSid: sid, outcome: resolved });
        }, 2500);
      } else if (onOutcome) {
        onOutcome({ callSid: "", outcome: resolved });
      }
      // Auto-reset the button back to idle a few seconds after the call ends.
      setTimeout(() => {
        if (!mountedRef.current) return;
        connectionRef.current = null;
        acceptedRef.current = false;
        setSeconds(0);
        setOutcome("");
        setPhase(PHASE.IDLE);
      }, 6000);
    },
    [onOutcome, stopTimer]
  );

  const attachConnection = useCallback(
    (connection) => {
      connectionRef.current = connection;
      acceptedRef.current = false;

      connection.on("ringing", () => {
        if (mountedRef.current) setPhase(PHASE.RINGING);
      });
      connection.on("accept", () => {
        acceptedRef.current = true;
        if (!mountedRef.current) return;
        setPhase(PHASE.CONNECTED);
        setSeconds(0);
        stopTimer();
        timerRef.current = setInterval(() => {
          setSeconds((prev) => prev + 1);
        }, 1000);
      });
      connection.on("disconnect", () => {
        settleOutcome(acceptedRef.current ? "answered" : "no_answer", connection);
      });
      connection.on("cancel", () => {
        settleOutcome("no_answer", connection);
      });
      connection.on("reject", () => {
        settleOutcome("busy", connection);
      });
      connection.on("error", () => {
        settleOutcome("failed", connection);
      });
    },
    [settleOutcome, stopTimer]
  );

  const dialInApp = useCallback(async () => {
    primeVoiceAudio(); // first user gesture — unblock ring audio + ask for notifications
    setHint("");
    setOutcome("");
    setPhase(PHASE.CONNECTING);
    try {
      const device = await ensureVoiceReady(tenantId);
      const fromNumber = fromNumberProp || (await fetchDefaultFromNumber());
      const connection = await placeOutboundCall({
        device,
        tenantId,
        userId,
        fromNumber,
        toNumber: phone,
      });
      attachConnection(connection);
      // Hand the live call to the global in-call overlay (the shared screen).
      try {
        window.dispatchEvent(
          new CustomEvent("voice-call-started", {
            detail: { connection, callee: { name: calleeName || "", number: phone } },
          })
        );
      } catch {
        /* overlay is best-effort */
      }
      if (mountedRef.current) setPhase(PHASE.RINGING);
    } catch (err) {
      // Graceful degradation to a tel: link.
      if (!mountedRef.current) return;
      const code = err?.code || err?.message || "voice_unavailable";
      if (code === "microphone_permission_denied") {
        setHint("Microphone blocked - opening phone app");
      } else {
        setHint("In-app calling unavailable - opening phone app");
      }
      setPhase(PHASE.FALLBACK);
      if (phone) window.location.href = `tel:${phone}`;
    }
  }, [attachConnection, calleeName, fromNumberProp, phone, tenantId, userId]);

  const telHref = phone ? `tel:${phone}` : undefined;
  const disabled = !phone;

  // Live states: the shared in-call overlay owns the controls (mute / keypad /
  // hold / End) and the timer, so the card button just reflects "on call" and
  // keeps its place — no duplicate End button, no layout jump.
  if (phase === PHASE.RINGING || phase === PHASE.CONNECTED || phase === PHASE.CONNECTING) {
    return (
      <span className={`c2c-wrap ${className}`}>
        <button type="button" className="c2c-btn" disabled aria-live="polite">
          <Phone size={15} />
          On call
        </button>
      </span>
    );
  }

  // Ended: show the recorded outcome briefly.
  if (phase === PHASE.ENDED && outcome) {
    const tone = OUTCOME_TONE[outcome] || "warn";
    return (
      <span className={`c2c-live ${className}`}>
        <span className={`c2c-outcome c2c-outcome--${tone}`}>
          {OUTCOME_LABELS[outcome] || outcome}
        </span>
      </span>
    );
  }

  // Idle / fallback: the call button (fallback keeps a tel: hint visible).
  return (
    <span className={`c2c-wrap ${className}`}>
      <button
        type="button"
        className="c2c-btn"
        onClick={dialInApp}
        disabled={disabled}
        title={disabled ? "No phone number" : "Call in-app"}
      >
        <Phone size={15} />
        {label}
      </button>
      {phase === PHASE.FALLBACK && telHref ? (
        <a className="c2c-fallback" href={telHref}>
          {hint || "Open phone app"}
        </a>
      ) : null}
    </span>
  );
}
